/**
 * The error model, client side.
 *
 * Every failure the console can encounter — a 403 from the API, a rate limit, a
 * dropped connection, an aborted request — is normalised into one class. That is
 * the point of the API's RFC 9457 output: the console has exactly one place that
 * reads an error response, and no component ever branches on "is this a Hono
 * error or a Zod error or something the network did".
 *
 * ## Status 0
 *
 * A request that never reached the server has no HTTP status. Rather than
 * inventing a fake one, `ApiProblem` uses 0 and a synthetic
 * `https://keystone.dev/errors/network` type, so a component can still switch on
 * `status` without a null check and `status >= 400` is false for it — which is
 * the correct answer to "did the server reject this?".
 *
 * ## `code` versus `status`
 *
 * `status` is the HTTP-level outcome. `code` is the business outcome, and several
 * business outcomes share a status. `last_owner` and `forbidden` are both 403,
 * and the UI must render them completely differently: one is a dead end, the
 * other is a fixable mistake. Components switch on `code`; only generic error
 * handling switches on `status`.
 */

export interface FieldError {
  path: string;
  message: string;
  code?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export class ApiProblem extends Error {
  readonly status: number;
  readonly type: string;
  readonly title: string;
  readonly code: string;
  readonly detail: string;
  readonly instance: string;
  readonly requestId: string | null;
  readonly errors: FieldError[];
  /** From `Retry-After`, when the server sent one. Seconds, not a date. */
  readonly retryAfterSeconds: number | null;

  constructor(init: {
    status: number;
    type: string;
    title: string;
    code: string;
    detail: string;
    instance?: string;
    requestId?: string | null;
    errors?: FieldError[];
    retryAfterSeconds?: number | null;
  }) {
    super(init.detail);
    this.name = 'ApiProblem';
    this.status = init.status;
    this.type = init.type;
    this.title = init.title;
    this.code = init.code;
    this.detail = init.detail;
    this.instance = init.instance ?? '';
    this.requestId = init.requestId ?? null;
    this.errors = init.errors ?? [];
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
  }

  /**
   * Parses a problem document, falling back to something useful when the body is
   * not one.
   *
   * The fallback matters more than it looks. A 502 from an intermediary, a
   * Cloudflare error page, an HTML body from a misconfigured proxy — none of
   * those are problem documents, and a parser that assumed the shape would throw
   * inside its own error path. The raw body is truncated into `detail` instead,
   * so the reader sees what actually came back.
   */
  static from(response: Response, body: unknown, rawText: string): ApiProblem {
    const retryAfterSeconds = parseRetryAfter(response.headers.get('Retry-After'));
    const requestId = response.headers.get('X-Request-Id');

    // `body` is narrowed to `Record<string, unknown>` by `isRecord`, and the two
    // `typeof` checks below narrow `detail` and `status` in the same expression.
    // Everything else is read defensively rather than cast, because this parser
    // runs inside the error path — a `TypeError` here would replace a useful
    // failure with an unexplained one.
    if (isRecord(body) && typeof body.detail === 'string' && typeof body.status === 'number') {
      const title = typeof body.title === 'string' ? body.title : response.statusText;
      return new ApiProblem({
        status: body.status,
        type: typeof body.type === 'string' ? body.type : 'about:blank',
        title,
        code: typeof body.code === 'string' ? body.code : slugify(title || 'error'),
        detail: body.detail,
        instance: typeof body.instance === 'string' ? body.instance : '',
        requestId: typeof body.requestId === 'string' ? body.requestId : requestId,
        errors: normaliseFieldErrors(body.errors),
        retryAfterSeconds,
      });
    }

    return new ApiProblem({
      status: response.status,
      type: 'about:blank',
      title: response.statusText || 'Request failed',
      code: `http_${response.status}`,
      detail:
        rawText.trim().length > 0
          ? truncate(rawText.trim(), 400)
          : `The server returned ${response.status} with an empty body.`,
      requestId,
      retryAfterSeconds,
    });
  }

  /** A request that never produced a response. */
  static network(cause: unknown): ApiProblem {
    const message = cause instanceof Error ? cause.message : String(cause);
    return new ApiProblem({
      status: 0,
      type: 'https://keystone.dev/errors/network',
      title: 'Network error',
      code: 'network_error',
      detail:
        'The request could not reach the API. Check your connection, and check that the API is ' +
        `running if you are developing locally. (${message})`,
    });
  }

  /**
   * Field errors keyed by JSON path, which is what a form wants.
   *
   * A path like `body.email` is normalised to `email`: the wire path names the
   * location in the request, but a form field is named after the field. Without
   * this, every form would strip the prefix itself and one of them would forget.
   */
  fieldErrors(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const error of this.errors) {
      const key = error.path.replace(/^(body|query|param|params|header)\./, '');
      // First error wins. Zod can emit several for one field and a form can only
      // show one; showing the last would surface the least specific.
      if (!(key in map)) map[key] = error.message;
    }
    return map;
  }

  /** True when re-authenticating might fix it. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isNetworkFailure(): boolean {
    return this.status === 0;
  }

  /**
   * A sentence to show the user.
   *
   * `detail` is written for a developer reading a log — "Use lowercase letters,
   * digits and single hyphens" is fine, but "The `alg` claim was rejected before
   * the key was loaded" is not. In practice the API's `detail` strings are
   * user-facing by design, so this passes them through and only adds context
   * where the status alone is unhelpful.
   */
  userMessage(): string {
    if (this.isNetworkFailure) return this.detail;
    if (this.isRateLimited) {
      const wait =
        this.retryAfterSeconds !== null
          ? ` Try again in ${this.retryAfterSeconds} second${this.retryAfterSeconds === 1 ? '' : 's'}.`
          : ' Try again shortly.';
      return `${this.detail}${wait}`;
    }
    return this.detail;
  }
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function normaliseFieldErrors(value: unknown): FieldError[] {
  if (!Array.isArray(value)) return [];
  const errors: FieldError[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    if (typeof entry.path !== 'string' || typeof entry.message !== 'string') continue;
    errors.push({
      path: entry.path,
      message: entry.message,
      ...(typeof entry.code === 'string' ? { code: entry.code } : {}),
    });
  }
  return errors;
}

/**
 * `Retry-After` is defined as either a number of seconds or an HTTP date, and
 * servers send both. Only the numeric form is handled; the date form is rare
 * enough here that guessing at a clock skew would be worse than falling back to
 * the generic "try again shortly".
 */
function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number.parseInt(header.trim(), 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}...`;
}

/** Narrows an unknown rejection to a problem, for use in a catch block. */
export function asProblem(error: unknown): ApiProblem {
  if (error instanceof ApiProblem) return error;
  if (error instanceof Error) {
    return new ApiProblem({
      status: 0,
      type: 'https://keystone.dev/errors/unknown',
      title: 'Unexpected error',
      code: 'unexpected_error',
      detail: error.message,
    });
  }
  return new ApiProblem({
    status: 0,
    type: 'https://keystone.dev/errors/unknown',
    title: 'Unexpected error',
    code: 'unexpected_error',
    detail: String(error),
  });
}

/** True for an aborted fetch, which is a normal outcome rather than a failure. */
export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
